import type { UseQueryResult } from "@tanstack/react-query";

export interface ChunkQueriesResult<TData> {
    /** Each chunk's data, its own or kept from before, in the queries' order. */
    responses: Array<TData | undefined>;
    /** Each chunk's data for its own query, fetched or derived exactly; undefined while it loads or shows data kept from before. */
    ownResponses: Array<TData | undefined>;
    /** Each chunk's error once its request fails, null otherwise. */
    errors: Array<Error | null>;
    /** Some chunk still shows data kept from before. */
    isPlaceholderData: boolean;
    /** Some chunk is still loading with nothing to show; false once its request fails. */
    isLoading: boolean;
}

/** A chunk's placeholder while it loads: data kept from another query, or derived exactly for its own. */
export interface ChunkPlaceholder<TData> {
    data: TData;
    isExact: boolean;
}

/** `useQueries` `combine` for one query per chunk of a range, with each query's placeholder in order. */
export const combineChunkQueries = <TData>({
    results,
    placeholders,
}: {
    results: ReadonlyArray<UseQueryResult<TData>>;
    placeholders: ReadonlyArray<ChunkPlaceholder<TData> | undefined>;
}): ChunkQueriesResult<TData> => {
    const isKept = (result: UseQueryResult<TData>, index: number) =>
        result.isPlaceholderData && !placeholders[index]?.isExact;
    return {
        responses: results.map(({ data }) => data),
        ownResponses: results.map((result, index) =>
            isKept(result, index) ? undefined : result.data,
        ),
        errors: results.map(({ error }) => error),
        isPlaceholderData: results.some(isKept),
        isLoading: results.some(({ isLoading }) => isLoading),
    };
};
